import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { ThumbsUp, ThumbsDown } from "lucide-react-native";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useSexualHealth } from "../SexualHealthContext";
import { buildSexualHealthNavigationStack } from "../buildSexualHealthNavigationStack";
import type { SexualHealthStackParamList } from "../SexualHealthNavigator";

export function ErectileDifficultiesScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useSexualHealth();
  const navigation =
    useNavigation<NativeStackNavigationProp<SexualHealthStackParamList>>();

  const selection = useMemo(() => {
    if (state.hasDifficultiesWithErection == null) return null;
    return state.hasDifficultiesWithErection ? "yes" : "no";
  }, [state.hasDifficultiesWithErection]);

  const handleChange = (value: "yes" | "no") => {
    updateState({ hasDifficultiesWithErection: value === "yes" });
  };

  const handleSave = async () => {
    await save();
    const stack = buildSexualHealthNavigationStack(state);
    const currentIdx = stack.indexOf("ErectileDifficulties");
    const nextRoute = stack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  return (
    <QuestionLayout
      title={t("med_history.gynae.sexual_health.erectile_difficulties_title")}
      subtitle={t(
        "med_history.gynae.sexual_health.erectile_difficulties_subtitle",
      )}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={selection === null}
      saveLabel={t("med_history.gynae.sexual_health.continue")}
    >
      <View style={styles.container}>
        <View style={styles.yesNoRow}>
          <SquareOptionButton
            icon={ThumbsUp}
            label={t("med_history.shared.yes")}
            isSelected={selection === "yes"}
            onPress={() => handleChange("yes")}
            iconSize={24}
            containerSize={48}
            labelSize="lg"
          />
          <SquareOptionButton
            icon={ThumbsDown}
            label={t("med_history.shared.no")}
            isSelected={selection === "no"}
            onPress={() => handleChange("no")}
            iconSize={24}
            containerSize={48}
            labelSize="lg"
          />
        </View>
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
  },
  yesNoRow: {
    flexDirection: "row",
    gap: 12,
    width: "100%",
  },
});
